import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlusIcon, 
  EnvelopeIcon, 
  UserIcon, 
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

/**
 * Form component for creating new users with content roles
 */
const CreateUserForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'CONTENT_GENERATOR',
    enabled: true,
    sendWelcomeEmail: true
  });

  const roleOptions = [
    {
      value: 'CONTENT_SUPERVISOR',
      label: 'Content Supervisor',
      description: 'Manages KITs, assigns work, and oversees content workflow',
      endpoint: '/api/super-admin/admins/content-supervisor'
    },
    {
      value: 'CONTENT_GENERATOR',
      label: 'Content Generator',
      description: 'Creates questions and works with assigned KITs',
      endpoint: '/api/super-admin/admins/content-generator'
    },
    {
      value: 'CONTENT_REVIEWER',
      label: 'Content Reviewer',
      description: 'Reviews and approves questions for publication',
      endpoint: '/api/super-admin/admins/content-reviewer'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Find the selected role configuration
      const selectedRole = roleOptions.find(role => role.value === formData.role);
      
      if (!selectedRole) {
        throw new Error('Invalid role selected');
      }

      // Prepare request data
      const requestData = {
        name: formData.name,
        email: formData.email,
        enabled: formData.enabled,
        sendWelcomeEmail: formData.sendWelcomeEmail
      };

      console.log('Creating user with role:', formData.role);
      console.log('Using endpoint:', selectedRole.endpoint);
      console.log('Request data:', requestData);

      // Make API call to the role-specific endpoint
      const response = await api.post(selectedRole.endpoint, requestData);

      console.log('User creation response:', response.data);

      setSuccess(`${selectedRole.label} created successfully! ${formData.sendWelcomeEmail ? 'Setup email has been sent.' : ''}`);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        role: 'CONTENT_GENERATOR',
        enabled: true,
        sendWelcomeEmail: true
      });

      // Redirect to users list after a delay
      setTimeout(() => {
        navigate('/users');
      }, 2000);

    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to create user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedRoleConfig = roleOptions.find(role => role.value === formData.role);

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <UserPlusIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Create New Content User</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Create a new user with content management roles
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-3" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
              <span className="text-sm text-green-700">{success}</span>
            </div>
          )}

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              <UserIcon className="h-4 w-4 inline mr-1" />
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter full name"
            />
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              <EnvelopeIcon className="h-4 w-4 inline mr-1" />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
          </div>

          {/* Role Selection */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
              <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
              Role
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {roleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {selectedRoleConfig && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedRoleConfig.description}
              </p>
            )}
          </div>

          {/* Role Permissions Info */}
          {selectedRoleConfig && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                {selectedRoleConfig.label} Permissions:
              </h4>
              <div className="text-sm text-blue-800">
                {formData.role === 'CONTENT_SUPERVISOR' && (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Create and delete KITs</li>
                    <li>Assign work to Content Generators and Reviewers</li>
                    <li>Approve and publish questions to main question bank</li>
                    <li>Access all questions with filter criteria</li>
                    <li>Manage team assignments and workflow</li>
                  </ul>
                )}
                {formData.role === 'CONTENT_GENERATOR' && (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Create questions using existing workflows</li>
                    <li>Convert PDFs to MCQs</li>
                    <li>Upload questions via Excel</li>
                    <li>Work with assigned KITs</li>
                    <li>Generate KIT deletion requests</li>
                  </ul>
                )}
                {formData.role === 'CONTENT_REVIEWER' && (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Edit existing questions and update status</li>
                    <li>Mark questions with various review states</li>
                    <li>Provide feedback and comments</li>
                    <li>Inform Supervisor when review is complete</li>
                    <li>Access assigned questions with filter criteria</li>
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Options */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enabled"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
                Enable user account
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="sendWelcomeEmail"
                name="sendWelcomeEmail"
                checked={formData.sendWelcomeEmail}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="sendWelcomeEmail" className="ml-2 text-sm text-gray-700">
                Send setup email with secure password creation link
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/users')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <UserPlusIcon className="h-4 w-4 inline mr-2" />
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserForm;
